import { Component } from '@theme/component';

/**
 * APC Email Sign Up component for source tracking and validation.
 *
 * - Client-side email validation with inline error + submit button toggle
 * - Stores the email in sessionStorage before native form submission
 * - After the page reloads with a successful post, calls the Gadget endpoint
 * - Scrolls section into view on success or error
 *
 * @typedef {object} ApcEmailSignUpRefs
 * @property {HTMLInputElement} [emailInput] - The email input (absent on confirmation state).
 * @property {HTMLButtonElement} [submitButton] - The submit button.
 * @property {HTMLElement} [emailError] - The inline validation error element.
 *
 * @extends Component<ApcEmailSignUpRefs>
 */
class ApcEmailSignUp extends Component {
  static GADGET_ENDPOINT = '/apps/apa/api/newsletter-source';

  #config = null;

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#parseConfig();

    const hasPosted = this.#config.formPostedSuccessfully;
    const hasErrors = window.location.href.includes('&form_type=customer');

    // Scroll into view on success or server-side error
    // Target the shopify-section wrapper since this element has display: contents
    if (hasPosted || hasErrors) {
      const section = this.closest('.shopify-section');
      (section || this).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // After successful submission (page reload), send source to Gadget
    if (hasPosted) {
      this.#handlePostSuccess();
      return;
    }

    // Set up form handling and validation
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.#handleFormSubmit);
    }

    if (this.refs.emailInput) {
      this.#setupEmailValidation();
    }
  }

  /**
   * Parses the JSON config embedded in the component.
   * @returns {object}
   */
  #parseConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return {};

    const config = JSON.parse(script.textContent);

    // Detect a successful post. The block emits a `formPostedSuccessfully` flag
    // from Liquid; the section signals it via the rendered confirmation element.
    config.formPostedSuccessfully =
      config.formPostedSuccessfully === true || this.querySelector('.apc-email-sign-up-section__confirmation') !== null;

    return config;
  }

  /**
   * Gets the sessionStorage key scoped to this section instance.
   * @returns {string}
   */
  get #storageKey() {
    return `apc-email-sign-up-email-${this.#config.sectionId || 'default'}`;
  }

  /**
   * Sets up client-side email validation.
   * Disables submit button until a valid email is entered.
   */
  #setupEmailValidation() {
    const { emailInput, submitButton, emailError } = this.refs;
    if (!submitButton) return;

    submitButton.disabled = true;

    emailInput?.addEventListener('input', () => {
      const isValid = emailInput.value.trim() !== '' && emailInput.validity.valid;
      submitButton.disabled = !isValid;

      if (emailError) {
        emailError.textContent = '';
      }
    });

    emailInput?.addEventListener('blur', () => {
      if (emailInput.value.trim() !== '' && !emailInput.validity.valid && emailError) {
        emailError.textContent = emailInput.validationMessage;
      }
    });
  }

  /**
   * Stores the email in sessionStorage before the native form submits.
   * Does not prevent default — the form submits normally.
   * @param {SubmitEvent} _event
   */
  #handleFormSubmit = (_event) => {
    const email = this.refs.emailInput?.value;
    if (email) {
      sessionStorage.setItem(this.#storageKey, email);
    }
  };

  /**
   * After a successful form submission (page reload), retrieves the
   * stored email and sends it with the source to the Gadget endpoint.
   */
  async #handlePostSuccess() {
    const email = sessionStorage.getItem(this.#storageKey);
    const { source } = this.#config;

    // Always clean up sessionStorage
    sessionStorage.removeItem(this.#storageKey);

    if (!email || !source) return;

    try {
      await fetch(ApcEmailSignUp.GADGET_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      });
    } catch (error) {
      console.error('APC Email Sign Up: Failed to store source metafield:', error);
    }
  }
}

if (!customElements.get('apc-email-sign-up')) {
  customElements.define('apc-email-sign-up', ApcEmailSignUp);
}

export { ApcEmailSignUp };
